/**
 * Service catalog — single source of truth for the detailed service slides.
 *
 * Each entry maps to a route /services/:slug and renders <ServiceDetail>.
 * Booking CTAs reuse the existing /book?service=<bookingKey> flow.
 */

export const SERVICE_CATALOG = [
    {
        slug: "free-consultation",
        bookingKey: "free_consultation",
        title: "Free 20-Minute Consultation",
        eyebrow: "Step 01 · Start here",
        summary:
            "A short introductory call to understand your move and identify the right next step.",
        bestFor: [
            "First-time inquiries",
            "Understanding service options",
            "Explaining your situation",
            "Deciding whether paid support is needed",
        ],
        includes: [
            "Basic intake",
            "Move type review",
            "General service explanation",
            "Recommended next step",
        ],
        excludes: [
            "Detailed relocation planning",
            "Rental research",
            "Vendor coordination",
            "Written shortlist",
            "Move-day planning",
        ],
        price: "Free",
        priceNote: "Up to 20 minutes",
        type: "free",
        cta: "Book Free Consultation",
        sticker: "check",
        accentStickers: ["mover", "spark"],
        tag: "FREE",
    },
    {
        slug: "paid-consultation",
        bookingKey: "paid_consultation",
        title: "Paid Relocation Consultation",
        eyebrow: "Step 02 · Plan with us",
        summary:
            "A deeper planning session for clients who need structured help thinking through their move, timeline, options, and support needs.",
        bestFor: [
            "International clients moving to Toronto",
            "Clients relocating from another Canadian city",
            "Remote planning",
            "Complex moves",
            "Clients who need a clear next-step plan",
        ],
        includes: [
            "Detailed discussion of your move",
            "Timeline review",
            "Needs assessment",
            "Service planning",
            "Practical next-step recommendations",
        ],
        price: "$75",
        priceNote: "per hour · CAD",
        type: "paid",
        cta: "Book Paid Consultation",
        sticker: "boxes",
        accentStickers: ["arrow", "heart"],
        tag: "PAID",
    },
    {
        slug: "shortlisting",
        bookingKey: "shortlisting",
        title: "Rental Shortlisting Support",
        eyebrow: "Search · Narrow",
        summary:
            "We help narrow options based on your stated needs — location, timing, budget, rental preferences, moving needs, or service requirements.",
        bestFor: [
            "Remote clients",
            "Newcomers to Toronto",
            "Clients overwhelmed by too many options",
            "Clients who need organized decision support",
        ],
        includes: [
            "Up to 2 hours of shortlisting work",
            "Organized list of options",
            "Basic notes based on agreed criteria",
            "Follow-up summary",
        ],
        price: "$150",
        priceNote: "for 2 hours · CAD",
        additionalTime:
            "Additional shortlisting time is billed at $75/hour only with client approval.",
        type: "paid",
        cta: "Request Shortlisting Support",
        sticker: "house",
        accentStickers: ["leaf", "spark"],
        tag: "PAID",
    },
    {
        slug: "viewing",
        bookingKey: "viewing_on_behalf",
        title: "Viewing on Behalf",
        eyebrow: "We attend · You stay informed",
        summary:
            "For clients who cannot attend in person, AllTask Moves can attend local viewings within the agreed time block and provide practical observations.",
        bestFor: [
            "International clients",
            "Out-of-town clients",
            "Clients unable to attend in person",
            "Rental search support",
        ],
        includes: [
            "Up to 4 hours",
            "Up to 3 viewings",
            "GTA +/- 10 km coverage",
            "Basic viewing notes",
            "Photos or videos where permitted",
            "Landlord / listing contact connection where appropriate",
        ],
        price: "$300",
        priceNote: "for 4 hours, up to 3 viewings · CAD",
        additionalTime:
            "Additional viewing time is billed at $75/hour only with client approval.",
        boundary:
            "AllTask Moves provides viewing support and coordination. We do not provide professional property inspection, legal advice, or guaranteed rental approval.",
        type: "paid",
        cta: "Request Viewing Support",
        sticker: "keys",
        accentStickers: ["house", "heart"],
        tag: "PAID",
    },
    {
        slug: "landlord-connection",
        bookingKey: "landlord_connection",
        title: "Landlord-Client Connection Support",
        eyebrow: "Handoff · Communication",
        summary:
            "Where appropriate, we help connect the client with the landlord, property manager, or listing contact so communication can continue clearly.",
        bestFor: [
            "Clients using shortlisting support",
            "Clients using viewing support",
            "Remote clients who need clearer handoff",
            "Clients who need basic communication support",
        ],
        includes: [
            "Contact handoff",
            "Basic communication support",
            "Next-step clarification",
        ],
        price: "Included",
        priceNote: "where relevant as part of shortlisting or viewing support",
        boundary:
            "AllTask Moves facilitates communication. We do not act as a licensed real estate representative unless such services are provided through a qualified licensed professional.",
        type: "quote",
        cta: "Request Support",
        sticker: "arrow",
        accentStickers: ["mover", "spark"],
        tag: "QUOTE",
    },
    {
        slug: "move-day",
        bookingKey: "move_day_4h",
        title: "Move-Day Coordination",
        eyebrow: "On the day · We coordinate",
        summary:
            "Move day can become stressful when timing, vendors, elevators, access, packing, cleaning, and communication are not aligned. We coordinate the agreed parts of your move day.",
        bestFor: [
            "Local Toronto / GTA moves",
            "Clients relocating into Toronto",
            "Busy clients",
            "Multi-vendor moves",
            "Moves requiring timing coordination",
        ],
        includes: [
            "Coordination within selected time block",
            "Vendor communication",
            "Client updates",
            "Timing support",
            "Issue tracking",
            "Practical move-day coordination",
        ],
        packages: [
            { label: "Basic coordination · 2 hours", price: "$200", key: "move_day_2h" },
            { label: "Half-day support · 4 hours", price: "$300", key: "move_day_4h" },
            { label: "Extended support · 6 hours", price: "$400", key: "move_day_6h" },
            { label: "Full-day support · 8 hours", price: "$500", key: "move_day_8h" },
            { label: "Heavy coordination · 10 hours", price: "$600", key: "move_day_10h" },
        ],
        price: "From $200",
        priceNote: "Choose 2 / 4 / 6 / 8 / 10 hour blocks",
        boundary:
            "Work is limited to the selected time block. Additional time requires client approval.",
        type: "paid",
        cta: "Book Move-Day Coordination",
        sticker: "truck",
        accentStickers: ["boxes", "check"],
        tag: "PAID",
    },
    {
        slug: "packing",
        bookingKey: "packing_support",
        title: "Packing Support Coordination",
        eyebrow: "Before move-day",
        summary:
            "We help coordinate packing support through available vendors or helpers, depending on the size, timing, and scope of your move.",
        bestFor: [
            "Busy clients",
            "Families",
            "Last-minute moves",
            "Clients needing extra hands before move day",
        ],
        includes: [
            "Scope review",
            "Vendor / helper coordination",
            "Timing support",
            "Basic communication",
        ],
        price: "Quote-based",
        priceNote: "Depending on scope, vendor availability, and time required",
        type: "quote",
        cta: "Request Packing Support",
        sticker: "boxes",
        accentStickers: ["mover", "spark"],
        tag: "QUOTE",
    },
    {
        slug: "cleaning",
        bookingKey: "cleaning_support",
        title: "Cleaning Support Coordination",
        eyebrow: "Move-in · Move-out",
        summary:
            "We help coordinate cleaning support before move-in, after move-out, or as part of arrival setup.",
        bestFor: [
            "Move-out cleaning",
            "Move-in cleaning",
            "Newcomers arriving in Toronto",
            "Clients who need cleaning arranged before arrival",
        ],
        includes: [
            "Scope review",
            "Cleaner / vendor coordination",
            "Timing confirmation",
            "Client updates",
        ],
        price: "Quote-based",
        priceNote: "Depending on scope, vendor availability, and property size",
        type: "quote",
        cta: "Request Cleaning Support",
        sticker: "spark",
        accentStickers: ["lamp", "leaf"],
        tag: "QUOTE",
    },
    {
        slug: "vendor-coordination",
        bookingKey: "vendor_coordination",
        title: "Local Vendor Coordination",
        eyebrow: "Trusted local network",
        summary:
            "We help coordinate local service providers where appropriate, depending on the client's move needs, timing, location, and vendor availability.",
        bestFor: [
            "Movers",
            "Packers",
            "Cleaners",
            "Furniture assembly providers",
            "Handyman services",
            "Junk removal providers",
            "Storage providers",
            "Local helpers",
        ],
        includes: [
            "Clear communication",
            "Vendor timing",
            "Scope alignment",
            "Client updates",
            "Practical coordination",
        ],
        price: "Quote-based",
        priceNote: "Or included where relevant depending on the service",
        type: "quote",
        cta: "Request Vendor Coordination",
        sticker: "chair",
        accentStickers: ["truck", "boxes"],
        tag: "QUOTE",
    },
];

export const findService = (slug) =>
    SERVICE_CATALOG.find((s) => s.slug === slug) || null;

export const findServiceByBookingKey = (key) =>
    SERVICE_CATALOG.find((s) => s.bookingKey === key) || null;
